import React, { useState, useRef } from 'react';
import { triggerContactService } from "@/components/ContactServiceModal";
import { useNavigate } from 'react-router-dom';
import { Typography, Card, Tag, Button, Space, Row, Col, message, Modal, Divider, Steps, Descriptions } from 'antd';
import {
  ScanOutlined, WifiOutlined, PrinterOutlined,
  FileProtectOutlined, SafetyOutlined, DownloadOutlined, ApiOutlined,
  CloudServerOutlined, ClusterOutlined, AppstoreOutlined, ToolOutlined,
  CrownOutlined, ThunderboltOutlined, LaptopOutlined, PlayCircleOutlined, CheckCircleOutlined,
  GlobalOutlined, SwapOutlined, DatabaseOutlined, FundOutlined,
  WindowsOutlined, AppleOutlined, LinuxOutlined, SettingOutlined,
  TeamOutlined, AuditOutlined, RadarChartOutlined, WechatOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { QRCodeSVG } from 'qrcode.react';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import { SalesCoach } from '@/components/SalesCoach';
import { GradeRibbon } from '@/pages/ProjectGrade/components/GradeRibbon';
import { SeoHelmet } from '@/pages/ProjectGrade/components/SeoHelmet';
import { billingAPI, extractApiError } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

const { Title, Paragraph, Text } = Typography;

const WEWORK_KF_URL = 'https://work.weixin.qq.com/kfid/kfce20d584b0179916f';
const PERSONAL_WECHAT_QR = '/wechat-qr.png';
const SERVICE_PHONE = '13599530881';

const FEATURES = [
  { icon: <ScanOutlined />, name: '硬件全扫描', desc: '一键采集CPU/主板/内存/硬盘/GPU/网卡/显示器/BIOS完整资产信息，支持Windows/Linux/macOS', color: '#6366f1' },
  { icon: <WifiOutlined />, name: '网络体检', desc: 'IP冲突检测/网关可达/DNS解析/SMB/RDP/WinRM 8项自动检测+交互式修复', color: '#06b6d4' },
  { icon: <PrinterOutlined />, name: '打印机一键共享', desc: '自动发现USB/LPT/网络打印机，智能配置共享+打印测试页，解决多电脑共享痛点', color: '#8b5cf6' },
  { icon: <FileProtectOutlined />, name: 'C盘安全清理', desc: '8项系统垃圾扫描+安全清理，不碰个人文件，删除走回收站，支持备份后清理', color: '#10b981' },
  { icon: <ClusterOutlined />, name: '资产智能报表', desc: '自动生成硬件资产列表，联网对比京东/中关村/太平洋实时价格，辅助IT采购决策', color: '#f59e0b' },
  { icon: <SwapOutlined />, name: '跨系统文件共享', desc: 'Windows SMB / Linux Samba+NFS / Web HTTP 三种方案，局域网内无缝传文件', color: '#ef4444' },
  { icon: <SafetyOutlined />, name: '权限一键修复', desc: '8项环境检测，管理权限/防火墙/WMI/执行策略问题自动修复，零门槛上手', color: '#3b82f6' },
  { icon: <AppstoreOutlined />, name: 'Web集中控制台', desc: '可视化仪表盘管理所有设备，下发上网管控/远程命令/批量任务，一目了然', color: '#ec4899' },
  { icon: <AuditOutlined />, name: '审计日志追踪', desc: '统一JSONL审计日志，本地+中心双写，操作可追溯，满足合规要求', color: '#14b8a6' },
  { icon: <GlobalOutlined />, name: '上网行为管控', desc: '按主机一键开关上网权限，防火墙规则+网关黑名单，支持定时策略', color: '#f97316' },
  { icon: <ToolOutlined />, name: 'C盘重复文件清理', desc: '按哈希识别重复文件，定位大文件Top20，休眠文件/还原点管理，节省磁盘空间', color: '#a855f7' },
  { icon: <LaptopOutlined />, name: '三大系统兼容', desc: 'Windows 10/11全版本(含家庭版自动配RDP) · Linux扫描 · macOS基础支持', color: '#64748b' },
];

const PRICING = [
  { id: 'free', name: '免费试用版', price: '0', period: '15天', originalPrice: '', features: ['完整硬件扫描', '网络体检(只读)', 'C盘清理(手动确认)', '单机使用', '社区支持'], tag: '推荐试用', tagColor: 'blue', btnText: '在线体验（免费）', isFree: true, icon: <DownloadOutlined /> },
  { id: 'pro', name: '专业版', price: '299', period: '永久', originalPrice: '竞品年费 ¥2,000+', discount: '省85%', discountColor: '#10b981', packageId: 'pro', features: ['免费版全部功能', '资产报表+实时价格对比', '打印机一键共享', '文件共享SMB配置', '权限自动修复', '3台设备授权', '邮件工单支持'], tag: '性价比之选', tagColor: 'green', btnText: '立即购买', icon: <CrownOutlined /> },
  { id: 'ent-premium', name: '旗舰版', price: '599', period: '永久', originalPrice: '竞品年费 ¥5,000+', discount: '省88%', discountColor: '#f59e0b', packageid: 'ent-premium', features: ['专业版全部功能', 'Web集中控制台', '批量任务下发', '上网行为管控', '审计日志追踪', '不限设备数', '优先技术支持'], tag: '企业推荐', tagColor: 'orange', btnText: '立即购买', icon: <ThunderboltOutlined />, highlighted: true },
  { id: 'enterprise', name: '团队版', price: '999', period: '永久', originalPrice: '竞品年费 ¥10,000+', discount: '省90%', discountColor: '#ef4444', packageId: 'enterprise', features: ['旗舰版全部功能', '不限席位', '多分支机构管理', '专属技术对接', '定制开发支持', 'SLA保障'], tag: '大团队', tagColor: 'red', btnText: '立即购买', icon: <TeamOutlined /> },
];

const ADVANTAGES = [
  { icon: <CheckCircleOutlined />, title: '零服务器成本', desc: '纯内网运行，无需购买服务器。脚本即跑即用，一台普通电脑即可做管理机' },
  { icon: <CheckCircleOutlined />, title: 'Win10/11全兼容', desc: '专业版/企业版/家庭版全支持。家庭版自动配置RDP Wrapper，远程桌面无障碍' },
  { icon: <CheckCircleOutlined />, title: '离线可用', desc: '数据不出公司内网，核心功能不依赖互联网。价格对比等功能离线时使用本地缓存数据' },
  { icon: <CheckCircleOutlined />, title: '永久买断', desc: '一次付费永久授权，无年费/无续费。对比竞品年费¥2,000-10,000+，1-3个月回本' },
];

const INSTALL_STEPS = [
  { step: 1, title: '下载脚本包', desc: '下载 jinwangtong-trial.zip 解压到 scripts\\ 目录' },
  { step: 2, title: '环境体检', desc: '右键以管理员运行 .\\health-check.ps1，6项全面体检' },
  { step: 3, title: '一键修复', desc: '运行 .\\health-check.ps1 -AutoFix 自动修复权限/防火墙/WMI等' },
  { step: 4, title: '全量扫描', desc: '运行 .\\scan-all.ps1 -IncludeSoftware 采集硬件+软件资产' },
  { step: 5, title: '开始管理', desc: '运行 .\\asset-manager.ps1 入库，启动 .\\console.ps1 打开Web控制台' },
];

const FAQ_ITEMS = [
  { q: '软件是否需要联网？', a: '核心功能(扫描、体检、共享、管控)纯内网运行，数据不出公司。价格对比功能需要联网查询，离线时使用本地缓存数据。' },
  { q: 'Windows 家庭版能用吗？', a: '可以。金网通自动检测系统版本，家庭版会自动配置RDP Wrapper以支持远程桌面功能。' },
  { q: '支持多少台电脑？', a: '专业版支持3台，旗舰版不限数量。团队版额外支持多分支机构管理。' },
  { q: 'License如何签发？', a: '购买后系统自动签发License到您的AIbak账号，下载脚本包后在任意电脑运行激活即可，无需人工联系。' },
  { q: '数据安全吗？', a: '所有数据存储在本地SQLite数据库，不上传到任何云端服务器。审计日志本地+中心双写，操作可追溯。' },
  { q: '能否跨网段管理？', a: '当前版本针对同一局域网设计。多分支机构管理请选择团队版，我们将提供定制方案。' },
  { q: '购买后可以退款吗？', a: '免费试用15天，满意再购买。购买后7天内可申请退款，详见退款政策。' },
  { q: '怎么联系技术支持？', a: '专业版及以上用户享受邮件工单支持。旗舰版和团队版享有优先技术支持。也可以在AI对话中联系在线客服。' },
];

const JinWangTongPage: React.FC = () => {
  const [buyLoading, setBuyLoading] = useState<string | null>(null);
  const [payQr, setPayQr] = useState<{ open: boolean; value: string; orderNo: string; pkgName: string } | null>(null);
  const pollRef = useRef<number | null>(null);
  const { trackAction, tips, showPanel, dismissTips } = usePurchaseIntent('jinwangtong', '金网通计算机管理系统');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPolling = (orderNo: string) => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      try {
        const r: any = await billingAPI.getOrderStatus(orderNo);
        if (r?.data?.status === 'paid') {
          stopPolling();
          setPayQr((prev) => prev ? { ...prev, open: false } : null);
          message.success('支付成功！License 已自动签发到您的账号');
        } else if (r?.data?.status === 'expired') {
          stopPolling();
          message.warning('订单已过期，请重新下单');
          setPayQr(null);
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const closePayQr = () => {
    stopPolling();
    setPayQr(null);
  };

  const handleBuy = async (pkgId: string, pkgName: string) => {
    trackAction('click_pricing', { comparedPlans: [pkgId] });
    if (!user) {
      Modal.confirm({ title: '请先登录', content: '购买金网通需要登录 AIbak 账号。', okText: '去登录', cancelText: '取消', onOk: () => navigate('/login') });
      return;
    }
    setBuyLoading(pkgId);
    try {
      const res: any = await billingAPI.createPrivateLicenseOrder({ packageId: pkgId });
      const payUrl = res?.data?.payParams?.code_url || res?.data?.payParams?.codeUrl || res?.data?.payUrl;
      const orderNo = res?.data?.orderNo as string;
      if (payUrl && orderNo) {
        setPayQr({ open: true, value: payUrl, orderNo, pkgName });
        startPolling(orderNo);
      } else {
        message.success('已创建 ' + pkgName + ' 订单，请在订单管理中完成支付');
        navigate('/profile?tab=orders');
      }
    } catch (err) { message.error(extractApiError(err, '创建订单失败，请稍后重试')); }
    finally { setBuyLoading(null); }
  };

  const openWechatService = () => {
    Modal.info({
      title: '联系客服',
      content: (
        <div style={{ textAlign: 'center' }}>
          <Paragraph>选择您方便的客服方式</Paragraph>
          <Button type="primary" icon={<WechatOutlined />} block size="large"
            onClick={() => triggerContactService()}
            style={{ marginBottom: 12, background: '#07c160', borderColor: '#07c160', borderRadius: 10, height: 44 }}>
            打开企业微信客服
          </Button>
          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>扫码添加个人微信</Paragraph>
          <img src={PERSONAL_WECHAT_QR} alt="个人微信二维码" style={{ width: 180, height: 180, borderRadius: 8, border: '1px solid #eee' }} />
          <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>服务热线：{SERVICE_PHONE}</Paragraph>
        </div>
      ),
      width: 340,
      okButtonProps: { style: { display: 'none' } },
      cancelText: '关闭',
      onCancel: () => {},
    });
  };

  // 在线体验 → 免费下载试用版 → 购买 → 自动签发 License → 下载完整安装包
  const handleViewDemo = () => {
    navigate("/jinwangtong-demo");
  };

  const handleFreeDownload = () => {
    window.open("/download/jinwangtong-trial.zip", "_blank");
    trackAction("download_trial");
    message.success("试用版下载已开始，15天全功能试用");
  };

  const handleBuyClick = (pkgId: string, pkgName: string) => {
    handleBuy(pkgId, pkgName);
  };
  

  return (
    <>
      <SeoHelmet
        title="金网通计算机管理系统 — 企业局域网互联互通一站式解决方案"
        description="金网通：企业局域网电脑互联互通系统。硬件全扫描、网络体检、打印机一键共享、C盘安全清理、资产智能报表、跨系统文件共享。永久买断 ¥299起，15天免费试用。"
        schemaJsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: '金网通计算机管理系统',
          description: '企业局域网电脑互联互通一站式解决方案：硬件全扫描、网络体检、打印机一键共享、C盘安全清理、资产智能报表、跨系统文件共享。永久买断授权，15天免费试用。',
          brand: { '@type': 'Brand', name: 'NexMind by AIbak' },
          category: '企业管理软件',
          url: 'https://aibak.site/jinwangtong',
          image: 'https://aibak.site/og-image.svg',
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: 'CNY',
            lowPrice: '299',
            highPrice: '999',
            offerCount: '4',
            offers: [
              { '@type': 'Offer', name: '专业版', price: '299', priceCurrency: 'CNY', availability: 'https://schema.org/InStock' },
              { '@type': 'Offer', name: '旗舰版', price: '599', priceCurrency: 'CNY', availability: 'https://schema.org/InStock' },
              { '@type': 'Offer', name: '团队版', price: '999', priceCurrency: 'CNY', availability: 'https://schema.org/InStock' },
            ],
          },
        }}
      />
    <div style={{ padding: '0 8px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #1a1a2e 70%, #16213e 100%)', borderRadius: 16, padding: '60px 40px', textAlign: 'center', position: 'relative', overflow: 'hidden', marginBottom: 24 }}>
        <div style={{ position: 'absolute', top: -50, right: -50, width: 300, height: 300, background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', borderRadius: '50%' }} />
        <div style={{ position: 'absolute', bottom: -30, left: -30, width: 250, height: 250, background: 'radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Tag color="purple" style={{ fontSize: 13, padding: '4px 16px', borderRadius: 20, marginBottom: 12 }}><ThunderboltOutlined /> 企业局域网管理 · 一站式解决方案</Tag>
          <Title level={1} style={{ color: '#fff', fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: 800, marginBottom: 16 }}>金网通计算机管理系统</Title>
          <Paragraph style={{ color: '#94a3b8', fontSize: 'clamp(15px, 2vw, 18px)', maxWidth: 700, margin: '0 auto 12px' }}>一套系统管理个人和企业多台电脑设备 · 企业局域网一键互联互通</Paragraph>
          <Paragraph style={{ color: '#64748b', fontSize: 14, maxWidth: 640, margin: '0 auto 24px' }}>一次部署全公司打通：文件共享 · 远程桌面 · 统一管控 · 审计溯源 · 上网管控 · 网络体检 — <Text strong style={{ color: '#818cf8' }}>六合一</Text> · 离线可用 · 数据不出公司</Paragraph>
          <Space size={16} wrap>
            <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={handleViewDemo} style={{ borderRadius: 10, height: 48, padding: '0 32px', fontSize: 16, fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', boxShadow: '0 4px 20px rgba(99,102,241,0.4)' }}>在线体验（免费演示）</Button>
          </Space>
          <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
            {[{ icon: <WindowsOutlined />, label: 'Win10/11全版本' }, { icon: <AppleOutlined />, label: 'macOS支持' }, { icon: <LinuxOutlined />, label: 'Linux扫描' }, { icon: <SafetyOutlined />, label: '离线可用' }, { icon: <ThunderboltOutlined />, label: '永久买断' }].map((item, i) => (<div key={i} style={{ textAlign: 'center' }}><div style={{ fontSize: 22, color: '#818cf8', marginBottom: 4 }}>{item.icon}</div><Text style={{ color: '#94a3b8', fontSize: 12 }}>{item.label}</Text></div>))}
          </div>
        </div>
      </div>

      {/* Advantages */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {ADVANTAGES.map((a, i) => (<Col xs={24} sm={12} md={6} key={i}><Card size="small" style={{ borderRadius: 12, border: '1px solid #e2e8f0', height: '100%' }} bodyStyle={{ padding: '20px 16px' }} hoverable><div style={{ color: '#6366f1', fontSize: 20, marginBottom: 8 }}>{a.icon}</div><Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>{a.title}</Text><Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>{a.desc}</Text></Card></Col>))}
      </Row>

      {/* 智评通信任背书 */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
        <GradeRibbon
          score={85.5}
          verdict="A"
          gateBlocked={null}
          projectName="金网通计算机管理系统"
          reportHref="/project-grade/demo"
          label="AIbak 智评通认证"
        />
      </div>

      {/* Features */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><Title level={2} style={{ marginBottom: 8 }}>核心功能</Title><Paragraph type="secondary" style={{ fontSize: 15 }}>覆盖企业局域网管理与IT资产运维的全场景需求</Paragraph></div>
        <Row gutter={[16, 16]}>
          {FEATURES.map((f, i) => (<Col xs={24} sm={12} md={8} lg={6} key={i}><Card hoverable style={{ borderRadius: 12, height: '100%', border: '1px solid #f1f5f9' }} bodyStyle={{ padding: '24px 20px' }}><div style={{ width: 44, height: 44, borderRadius: 12, background: f.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, fontSize: 20, color: f.color }}>{f.icon}</div><Text strong style={{ fontSize: 15, display: 'block', marginBottom: 6 }}>{f.name}</Text><Text type="secondary" style={{ fontSize: 12, lineHeight: 1.7 }}>{f.desc}</Text></Card></Col>))}
        </Row>
      </div>

      <Divider />

      {/* Pricing */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><Title level={2} style={{ marginBottom: 8 }}><CrownOutlined style={{ color: '#f59e0b', marginRight: 8 }} />选择适合您的版本</Title><Paragraph type="secondary" style={{ fontSize: 15 }}>一次付费 · 永久授权 · 自动签发 License · 无年费</Paragraph></div>
        <Row gutter={[16, 16]}>
          {PRICING.map((pkg) => (<Col xs={24} sm={12} md={6} key={pkg.id}><Card hoverable style={{ borderRadius: 16, border: pkg.highlighted ? '2px solid #6366f1' : '1px solid #e2e8f0', height: '100%', position: 'relative', overflow: 'hidden', boxShadow: pkg.highlighted ? '0 8px 32px rgba(99,102,241,0.15)' : undefined }} bodyStyle={{ padding: '24px 20px' }}>
            {pkg.highlighted && <div style={{ position: 'absolute', top: 0, right: 0, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', padding: '4px 20px', fontSize: 11, borderBottomLeftRadius: 12, fontWeight: 600 }}>最受欢迎</div>}
            <div style={{ textAlign: 'center', marginBottom: 16 }}><div style={{ fontSize: 28, color: '#6366f1', marginBottom: 8 }}>{pkg.icon}</div><Tag color={pkg.tagColor as any} style={{ marginBottom: 8 }}>{pkg.tag}</Tag><Title level={4} style={{ marginBottom: 4 }}>{pkg.name}</Title><div style={{ position: 'relative', display: 'inline-block', margin: '12px 0' }}>
                  {pkg.discount && (
                    <span style={{
                      position: 'absolute', top: -16, right: -28,
                      background: pkg.discountColor || '#ff5c1a',
                      borderRadius: '6px 6px 2px 6px',
                      padding: '2px 10px', color: '#fff', fontSize: 12, fontWeight: 800,
                      boxShadow: `0 3px 12px ${pkg.discountColor || '#ff5c1a'}44`,
                      letterSpacing: 0.5, whiteSpace: 'nowrap'
                    }}>
                      {pkg.discount}
                    </span>
                  )}
                  <Text style={{ fontSize: 36, fontWeight: 800, color: pkg.highlighted ? '#6366f1' : '#1e293b' }}>¥{pkg.price}</Text>
                  <Text type="secondary" style={{ fontSize: 14 }}>/{pkg.period}</Text>
                </div>
                {pkg.originalPrice && (
                  <div style={{ marginTop: -4 }}>
                    <Text delete type="secondary" style={{ fontSize: 12, color: '#94a3b8' }}>{pkg.originalPrice}</Text>
                  </div>
                )}</div>
            <div style={{ marginBottom: 16 }}>{pkg.features.map((feat, j) => (<div key={j} style={{ padding: '4px 0', display: 'flex', alignItems: 'flex-start', gap: 8 }}><CheckCircleOutlined style={{ color: '#10b981', marginTop: 3, flexShrink: 0 }} /><Text style={{ fontSize: 13, lineHeight: 1.5 }}>{feat}</Text></div>))}</div>
            <Button type={pkg.highlighted ? 'primary' : 'default'} block size="large" loading={buyLoading === pkg.packageId} onClick={() => pkg.isFree ? handleFreeDownload() : pkg.packageId && handleBuy(pkg.packageId, pkg.name)} style={{ borderRadius: 10, height: 44, fontWeight: 600, ...(pkg.highlighted ? { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none' } : {}) }} icon={pkg.isFree ? <DownloadOutlined /> : <CrownOutlined />}>{pkg.btnText}</Button>
          </Card></Col>))}
        </Row>
        <Paragraph type="secondary" style={{ textAlign: 'center', marginTop: 20, fontSize: 13 }}>购买后自动签发 License · 永久授权 · 支持微信支付/对公转账 · <a onClick={() => navigate('/ai-chat')} style={{ cursor: 'pointer', color: '#6366f1' }}>联系客服</a> 获取发票</Paragraph>
      </div>

      <Divider />

      {/* Install */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><Title level={2} style={{ marginBottom: 8 }}>快速开始</Title><Paragraph type="secondary" style={{ fontSize: 15 }}>4 步完成部署，无需专业IT背景</Paragraph></div>
        <Card style={{ borderRadius: 16, marginBottom: 24 }}><Steps current={-1} direction="vertical" size="small" items={INSTALL_STEPS.map(s => ({ title: <Text strong>{s.title}</Text>, description: <Text type="secondary">{s.desc}</Text> }))} style={{ maxWidth: 600, margin: '0 auto' }} /></Card>
        <div style={{ textAlign: 'center', marginTop: 24 }}><Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={handleViewDemo} style={{ borderRadius: 10, height: 48, padding: '0 32px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', fontWeight: 600, fontSize: 15 }}>在线体验（Web演示）</Button><Paragraph type="secondary" style={{ marginTop: 12, fontSize: 13 }}></Paragraph></div>
      </div>

      <Divider />

      {/* Specs */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><Title level={2} style={{ marginBottom: 8 }}>技术规格</Title></div>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}><Card title="系统要求" style={{ borderRadius: 16 }} size="small"><Descriptions column={1} size="small" bordered><Descriptions.Item label="操作系统">Windows 10/11 全版本(含家庭版)</Descriptions.Item><Descriptions.Item label="PowerShell">2.0+ 全兼容(自动降级适配)</Descriptions.Item><Descriptions.Item label="管理员权限">需要(首次部署使用)</Descriptions.Item><Descriptions.Item label="磁盘空间">脚本 &lt; 20MB</Descriptions.Item><Descriptions.Item label="网络">局域网 TCP/IP(无需互联网)</Descriptions.Item><Descriptions.Item label="额外扫描">Linux (bash) · macOS (beta)</Descriptions.Item></Descriptions></Card></Col>
          <Col xs={24} md={12}><Card title="能力边界" style={{ borderRadius: 16 }} size="small"><Descriptions column={1} size="small" bordered><Descriptions.Item label="扫描深度">CPU/主板/内存/硬盘/GPU/网卡/显示器/BIOS/外设</Descriptions.Item><Descriptions.Item label="网络协议">SMB/CIFS、RDP、WinRM、NFS、HTTP</Descriptions.Item><Descriptions.Item label="文件共享">Windows SMB / Linux Samba+NFS / Web HTTP</Descriptions.Item><Descriptions.Item label="打印机">USB/LPT/网络打印机自动发现+共享</Descriptions.Item><Descriptions.Item label="数据存储">SQLite 本地库 + JSON 报表导出</Descriptions.Item><Descriptions.Item label="安全架构">纯内网、数据不出公司、不依赖云服务</Descriptions.Item></Descriptions></Card></Col>
        </Row>
      </div>

      <Divider />

      {/* FAQ */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}><Title level={2} style={{ marginBottom: 8 }}>常见问题</Title></div>
        <Card style={{ borderRadius: 16 }}>
          {FAQ_ITEMS.map((item, i) => (<div key={i} style={{ marginBottom: i < 7 ? 16 : 0, padding: '12px 0', borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none' }}><Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4, color: '#1e293b' }}>Q: {item.q}</Text><Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>{item.a}</Text></div>))}
        </Card>
      </div>

      {/* CTA */}
      <div style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: 16, padding: '48px 32px', textAlign: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ color: '#fff', marginBottom: 8 }}>开始管理您的企业网络</Title>
        <Paragraph style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, marginBottom: 24 }}>在线体验满意后购买 · 永久买断 ¥299起 · 支付成功自动签发License</Paragraph>
        <Space size={16}>
          <Button size="large" icon={<DownloadOutlined />} onClick={handleFreeDownload} style={{ borderRadius: 10, height: 48, padding: '0 32px', fontSize: 16, fontWeight: 600, background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)' }}>免费下载试用</Button>
          <Button size="large" icon={<PlayCircleOutlined />} onClick={handleViewDemo} style={{ borderRadius: 10, height: 48, padding: '0 32px', fontSize: 16, fontWeight: 600, background: '#fff', color: '#6366f1', border: 'none' }}>在线体验</Button>
          <Button size="large" icon={<CrownOutlined />} loading={buyLoading === 'pro'} onClick={() => handleBuy('pro', '专业版')} style={{ borderRadius: 10, height: 48, padding: '0 32px', fontSize: 16, fontWeight: 600, background: '#10b981', color: '#fff', border: 'none' }}>立即购买 ¥299起</Button>
          <Button size="large" ghost icon={<WechatOutlined />} onClick={openWechatService} style={{ borderRadius: 10, height: 48, padding: '0 32px', fontSize: 15, color: '#fff', borderColor: 'rgba(255,255,255,0.6)' }}>联系客服</Button>
        </Space>
      </div>

      <div style={{ textAlign: 'center', padding: '16px 0 32px' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>金网通计算机管理系统 V2 · AIbak 旗下产品 · <a onClick={openWechatService} style={{ cursor: 'pointer' }}>技术支持</a> · <a onClick={() => navigate('/jinwangtong')} style={{ cursor: 'pointer' }}>查看定价</a></Text>
      </div>

      {/* 微信支付二维码弹窗 */}
      {payQr?.open && (
        <Modal open={payQr.open} title={`微信支付 · ${payQr.pkgName}`} footer={null} onCancel={closePayQr} width={360} centered>
          <div style={{ textAlign: 'center' }}>
            <Paragraph>请使用微信扫描下方二维码完成支付</Paragraph>
            <QRCodeSVG value={payQr.value} size={200} style={{ margin: '0 auto' }} />
            <Paragraph type="secondary" style={{ marginTop: 12, fontSize: 12 }}>支付完成后 License 将自动签发到您的账号</Paragraph>
            <Paragraph type="secondary" style={{ fontSize: 12 }}>订单号：{payQr.orderNo}</Paragraph>
          </div>
        </Modal>
      )}

      <SalesCoach tips={tips} visible={showPanel} onDismiss={dismissTips} productName="金网通计算机管理系统" />

    </div>
    </>
  );
};

export default JinWangTongPage;











